import { ApiErrorEnvelope } from './types';

export type PortalMode = 'all' | 'client' | 'admin';

const DEFAULT_API_BASE_URL = 'https://2.sync.cyberco.com';

export function getApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL;
  if (configured && configured.trim().length > 0) {
    return configured.trim().replace(/\/+$/, '');
  }
  return DEFAULT_API_BASE_URL;
}

export function getPortalMode(): PortalMode {
  const raw = (import.meta.env.VITE_PORTAL_MODE || '').toLowerCase().trim();
  if (raw === 'client' || raw === 'admin') {
    return raw;
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    if (host.startsWith('admin.') || host.includes('.admin.')) {
      return 'admin';
    }
    if (host === 'sync.cyberco.com' || host === '2.sync.cyberco.com') {
      return 'client';
    }
  }
  return 'all';
}

export const STORAGE_KEYS = {
  USER_TOKEN: 'sync_user_token',
  ADMIN_TOKEN: 'sync_admin_token',
  IMPERSONATION_TOKEN: 'sync_impersonation_token',
  IMPERSONATED_USER_LABEL: 'sync_impersonated_user_label',
  LEGACY_AUTH_TOKEN: 'auth-token-id',
} as const;

export type AuthScope = 'client' | 'admin' | 'auto';

export class ApiClientError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type UnauthorizedListener = (scope: 'client' | 'admin') => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

export function getClientToken(): string | null {
  return (
    localStorage.getItem(STORAGE_KEYS.IMPERSONATION_TOKEN) ||
    localStorage.getItem(STORAGE_KEYS.USER_TOKEN) ||
    localStorage.getItem(STORAGE_KEYS.LEGACY_AUTH_TOKEN)
  );
}

export function setClientToken(token: string | null): void {
  if (token) {
    localStorage.setItem(STORAGE_KEYS.USER_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.LEGACY_AUTH_TOKEN, token);
  } else {
    localStorage.removeItem(STORAGE_KEYS.USER_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.LEGACY_AUTH_TOKEN);
  }
}

export function getAdminToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN);
}

export function setAdminToken(token: string | null): void {
  if (token) {
    localStorage.setItem(STORAGE_KEYS.ADMIN_TOKEN, token);
  } else {
    localStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN);
  }
}

export function setImpersonationSession(token: string | null, userLabel?: string): void {
  if (token) {
    localStorage.setItem(STORAGE_KEYS.IMPERSONATION_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.LEGACY_AUTH_TOKEN, token);
    if (userLabel) {
      localStorage.setItem(STORAGE_KEYS.IMPERSONATED_USER_LABEL, userLabel);
    }
  } else {
    localStorage.removeItem(STORAGE_KEYS.IMPERSONATION_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.IMPERSONATED_USER_LABEL);
    const regularToken = localStorage.getItem(STORAGE_KEYS.USER_TOKEN);
    if (regularToken) {
      localStorage.setItem(STORAGE_KEYS.LEGACY_AUTH_TOKEN, regularToken);
    } else {
      localStorage.removeItem(STORAGE_KEYS.LEGACY_AUTH_TOKEN);
    }
  }
}

export function getImpersonationInfo(): { active: boolean; token: string | null; label: string | null } {
  const token = localStorage.getItem(STORAGE_KEYS.IMPERSONATION_TOKEN);
  const label = localStorage.getItem(STORAGE_KEYS.IMPERSONATED_USER_LABEL);
  return {
    active: Boolean(token),
    token,
    label,
  };
}

function resolveToken(scope: AuthScope): { token: string | null; resolvedScope: 'client' | 'admin' } {
  if (scope === 'admin') {
    return { token: getAdminToken() || getClientToken(), resolvedScope: 'admin' };
  }
  if (scope === 'client') {
    return { token: getClientToken(), resolvedScope: 'client' };
  }
  const isAdminPath =
    typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/admin') ||
      (getPortalMode() === 'admin' && !getImpersonationInfo().active));
  if (isAdminPath) {
    return { token: getAdminToken() || getClientToken(), resolvedScope: 'admin' };
  }
  return { token: getClientToken() || getAdminToken(), resolvedScope: 'client' };
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  scope?: AuthScope;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/**
 * Strips `undefined` keys from request payload objects so Rust's
 * `#[serde(deny_unknown_fields)]` only receives explicitly provided fields.
 */
function sanitizeBody(body: unknown): unknown {
  if (body === null || body === undefined || typeof body !== 'object' || Array.isArray(body)) {
    return body;
  }
  const cleaned: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(body as Record<string, unknown>)) {
    if (val !== undefined) {
      cleaned[key] = val;
    }
  }
  return cleaned;
}

export function buildUrl(
  path: string,
  query?: Record<string, string | number | boolean | undefined | null>,
): string {
  const base = getApiBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${base}${normalizedPath}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', query, body, scope = 'auto', signal, headers: extraHeaders } = options;
  const url = buildUrl(path, query);
  const { token, resolvedScope } = resolveToken(scope);

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...extraHeaders,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    headers['auth-token-id'] = token;
  }

  let serializedBody: string | undefined;
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    serializedBody = JSON.stringify(sanitizeBody(body));
  }

  const response = await fetch(url, {
    method,
    headers,
    body: serializedBody,
    signal,
  });

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const rawText = await response.text();

  let parsed: unknown = undefined;
  if (rawText.length > 0) {
    if (isJson || rawText.startsWith('{') || rawText.startsWith('[')) {
      try {
        parsed = JSON.parse(rawText);
      } catch {
        parsed = rawText;
      }
    } else {
      parsed = rawText;
    }
  }

  if (!response.ok) {
    if (response.status === 401) {
      unauthorizedListeners.forEach((fn) => fn(resolvedScope));
    }
    const envelope = parsed as Partial<ApiErrorEnvelope> | undefined;
    const code =
      (envelope && typeof envelope === 'object' && envelope.error?.code) ||
      `HTTP_${response.status}`;
    const message =
      (envelope && typeof envelope === 'object' && envelope.error?.message) ||
      (typeof parsed === 'string' && parsed.length > 0 ? parsed : response.statusText) ||
      `Request failed with status ${response.status}`;
    throw new ApiClientError(response.status, code, message, parsed);
  }

  return parsed as T;
}
